/**
 * 解开服务端封装的题面。对应 server/sealing.py。
 *
 * 格式：base64( iv(12) || AES-256-GCM 密文||tag )，AAD 是 "日期:slot"。
 *
 * 有 WebCrypto 就用 WebCrypto，没有就用纯 JS 实现（aesgcm.js）——
 * crypto.subtle 只在安全上下文存在，用局域网 IP 开页面调试时拿不到它，
 * 不兜底的话整个玩法页会直接开不出来。两条路解的是同一份报文。
 *
 * ⚠️ 这是混淆，不是保密。
 * 密钥就在下面这行里，谁都能从 JS 里翻出来。它挡的是「顺手抓一把接口响应
 * 就拖走整个题库」，挡不住铁了心要逆的人 —— 只要题目要画在屏幕上，
 * 当前这道题的题面就一定能被还原。真正管用的是服务端按 slot 下发：
 * 玩家手上任何时刻只有他正在看的那一道。
 *
 * 换密钥要前后端一起换：这里的 KEY_B64 与 server/sealing.py 的 PUZZLE_KEY 必须一致。
 */

import { aesGcmDecrypt } from './aesgcm.js';

const KEY_B64 = 'AoPBifG1Byyf5ZDfeS0zW+M3EcG5VlwIWsPJkLB/igQ=';

/** @type {Promise<CryptoKey> | null} */
let webKey = null;

function b64ToBytes(b64) {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

/**
 * @param {string} sealed base64 串
 * @param {string} date   题单日期，参与 AAD
 * @param {number} slot   题号，参与 AAD
 * @returns {Promise<any>} 解出的 JSON
 */
export async function unseal(sealed, date, slot) {
    const raw = b64ToBytes(sealed);
    const iv = raw.subarray(0, 12);
    const body = raw.subarray(12);
    const key = b64ToBytes(KEY_B64);
    const aad = new TextEncoder().encode(`${date}:${slot}`);

    // 注意别写成 crypto.subtle.decrypt(...) 直接调用：成员取值先于参数求值，
    // subtle 是 undefined 时会在守卫生效前就抛 TypeError
    const subtle = globalThis.crypto?.subtle;
    let plain;
    if (subtle) {
        webKey ??= subtle.importKey('raw', key, 'AES-GCM', false, ['decrypt']);
        plain = new Uint8Array(
            await subtle.decrypt({ name: 'AES-GCM', iv, additionalData: aad }, await webKey, body));
    } else {
        plain = aesGcmDecrypt(key, iv, body, aad);
    }
    return JSON.parse(new TextDecoder().decode(plain));
}
